// src/ShopifyOnboardingPage.js
import React, { useState, useEffect } from 'react';
import { callCompleteShopifyOnboarding } from './firebase';
import { supabase } from './supabaseClient';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './ShopifyOnboardingPage.css';
import { useI18n } from './i18n';

// Same gateway this dashboard already calls for the 4 Cloud-Function-replacement
// callables (see src/firebase.js) — the auto-provision check lives on the
// same Express gateway (confirming-app.dafagents.com), just outside the
// `/api/dashboard/*` callable convention (it's `/api/internal/...`, unauthenticated
// on purpose — see routes/confirmation-provision.js's own docstring).
const API_BASE = process.env.REACT_APP_CONFIRMATION_API_BASE;

/**
 * 🏗️ Daf One → Daf Confirmation auto-provision check (2026-07-07, frontend
 * wave). Calls `GET {API_BASE}/api/internal/onboarding-daf-one-check?shop=`
 * the moment this page mounts, BEFORE rendering the manual onboarding form.
 *
 * Contract (see DAF_CONFIRMATION_CONTEXT.md's "🏗️ الإنشاء التلقائي..."
 * section for the full backend-side writeup):
 *   → { autoProvision: false }                                  — fall back to the form, always
 *   → { autoProvision: true, token, needsOperationalSetup }      — redirect straight in
 *
 * FAIL-SAFE BY CONSTRUCTION: any network error, non-200, malformed JSON, or
 * missing `shop` resolves to "show the manual form" — this function never
 * throws to its caller, and it never blocks the form from eventually
 * rendering. Independent merchants (not coming from Daf One) always get
 * `{autoProvision:false}` from the backend itself (their shop isn't linked
 * in Daf One), so this call is a pure no-op for them beyond one extra network
 * round-trip before the form paints.
 *
 * @param {string} shop
 * @returns {Promise<{autoProvision: boolean, token?: string, needsOperationalSetup?: boolean}>}
 */
async function checkDafOneAutoProvision(shop) {
  if (!API_BASE || !shop) return { autoProvision: false };
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const res = await fetch(
      `${API_BASE}/api/internal/onboarding-daf-one-check?shop=${encodeURIComponent(shop)}`,
      { method: 'GET', signal: controller.signal }
    );
    clearTimeout(timeout);
    if (!res.ok) return { autoProvision: false };
    const body = await res.json();
    if (body && body.autoProvision === true && body.token) {
      return {
        autoProvision: true,
        token: body.token,
        needsOperationalSetup: !!body.needsOperationalSetup,
      };
    }
    return { autoProvision: false };
  } catch (err) {
    // Network error / timeout / CORS block / malformed response — always
    // fall back silently, never surface this to the merchant.
    console.log('ℹ️ Daf One auto-provision check unavailable, falling back to manual onboarding:', err?.message);
    return { autoProvision: false };
  }
}

const ShopifyOnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shop = searchParams.get('shop');
  const { t, lang, toggleLang } = useI18n();

  // 🏗️ Auto-provision check state — 'checking' (spinner, form hidden) →
  // 'redirecting' (auto-provision succeeded, about to hard-navigate away) →
  // 'form' (fall back to the manual onboarding form below, unchanged).
  const [autoProvisionPhase, setAutoProvisionPhase] = useState(shop ? 'checking' : 'form');

  useEffect(() => {
    if (!shop) {
      setAutoProvisionPhase('form');
      return;
    }
    let cancelled = false;
    checkDafOneAutoProvision(shop).then((result) => {
      if (cancelled) return;
      if (result.autoProvision) {
        setAutoProvisionPhase('redirecting');
        // Hard navigation (not react-router) — this hands off to
        // MagicLinkHandler ('/auth' route in App.js) which lives at the
        // app root, exchanging the token for a real session and then
        // routing into '/dashboard'. `embedded=1` preserves the existing
        // Daf One iframe-embed signal (see src/lib/embedMode.js) exactly
        // like the manual-onboarding → dashboard path already does today.
        window.location.href = `/auth?token=${encodeURIComponent(result.token)}&embedded=1`;
      } else {
        setAutoProvisionPhase('form');
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  const [formData, setFormData] = useState({
    brandName: '',
    email: '',
    password: '',
    telegramId: '',
    countryCode: '20',
    facebookUrl: '',
    instagramUrl: '',
    messageLanguage: 'ar'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorKey, setErrorKey] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'telegramId' ? value.replace(/\D/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorKey('');
    setLoading(true);

    try {
      // Validation
      if (!formData.brandName || !formData.email || !formData.password || !formData.telegramId) {
        setErrorKey('onboarding.error.missingFields');
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        setErrorKey('onboarding.error.passwordShort');
        setLoading(false);
        return;
      }

      console.log('🔄 Completing Shopify onboarding for:', shop);

      // استدعاء راوت الـ onboarding (fetch — بدل httpsCallable، مفيش
      // Authorization header هنا لأنه pre-auth: مفيش جلسة أصلًا لحد ما
      // الراوت يرجّع واحدة تحت).
      const result = await callCompleteShopifyOnboarding({
        shop: shop,
        brandName: formData.brandName,
        email: formData.email,
        password: formData.password,
        telegramId: formData.telegramId,
        countryCode: formData.countryCode,
        facebookUrl: formData.facebookUrl || '',
        instagramUrl: formData.instagramUrl || '',
        messageLanguage: formData.messageLanguage || 'ar'
      });

      if (!result.success) {
        setError(result.message || '');
        setErrorKey(result.message ? '' : 'onboarding.error.generic');
        throw new Error(result.message || 'onboarding.error.generic');
      }

      console.log('✅ Onboarding completed successfully:', result);

      // 🔒 قفل ثغرة uid-in-URL (Wave 3b): بدل ما نخزّن الباسورد plaintext
      // في localStorage ونعمل redirect بـ `?uid=<authUid>` (اللي الداشبورد
      // كان بيثق فيه بلا أي تحقق)، الراوت الجديد بيرجّع جلسة GoTrue حقيقية
      // (access/refresh token) — نبنيها هنا في المتصفح مباشرة عبر
      // supabase.auth.setSession، فمفيش أي معرّف هوية بيتمرّر عبر الـURL
      // خالص. الدخول بعد كده لـ `/dashboard` (بلا أي query param) —
      // getMyBrand() هو اللي بيحل البراند من الجلسة.
      if (result.session && result.session.access_token && result.session.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
        if (sessionError) {
          console.error('❌ Failed to establish session after onboarding:', sessionError);
          setErrorKey('onboarding.error.generic');
          return;
        }
        navigate('/dashboard');
      } else {
        // الراوت لسه ما بيرجّعش جلسة (السيرفر بيتبنى بالتوازي) — التاجر
        // يقدر يسجّل دخول يدوي بنفس الإيميل/الباسورد اللي كتبهم فوق.
        console.warn('⚠️ completeShopifyOnboarding لم يرجع جلسة — التوجيه لصفحة الدخول');
        navigate('/');
      }

    } catch (err) {
      console.error('❌ Onboarding error:', err);
      const message = err?.message;
      if (message && message.startsWith('onboarding.')) {
        setErrorKey(message);
        setError('');
      } else {
        setError(message || '');
        setErrorKey(message ? '' : 'onboarding.error.generic');
      }
    } finally {
      setLoading(false);
    }
  };

  // 🏗️ Auto-provision check in flight (or about to redirect) — show a
  // simple, branded loading state instead of the manual form. Never shown
  // for merchants without a `shop` param at all (autoProvisionPhase starts
  // as 'form' in that case, see useState initializer above), and always
  // falls through to the form below the moment the check resolves to
  // `autoProvision:false` — this branch NEVER blocks the existing manual
  // onboarding path, it only delays its first paint by one network round-trip.
  if (autoProvisionPhase === 'checking' || autoProvisionPhase === 'redirecting') {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '48px 24px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '50%',
            border: '4px solid #444', borderTopColor: '#6c5ce7',
            animation: 'rdspin 0.8s linear infinite'
          }} />
          <style>{`@keyframes rdspin{to{transform:rotate(360deg)}}`}</style>
          <p style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>{t('loading.autoProvision')}</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <h1 style={{ color: '#ff5555' }}>❌</h1>
          <p>{t('onboarding.error.missingShop')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            type="button"
            onClick={toggleLang}
            style={{
              background: '#2d2f3a',
              color: '#f8f8f2',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            aria-label={t('lang.toggle')}
          >
            <span aria-hidden="true">🌐</span>
            <span>{lang === 'en' ? 'EN' : 'AR'}</span>
          </button>
        </div>
        <div className="onboarding-header">
          <h1>🎉 {t('onboarding.title')}</h1>
          <p className="shop-name">{shop}</p>
          <p className="subtitle">{t('onboarding.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {(error || errorKey) && (
            <div className="error-message">
              ⚠️ {errorKey ? t(errorKey) : error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="brandName">
              {t('onboarding.brandName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="brandName"
              name="brandName"
              value={formData.brandName}
              onChange={handleInputChange}
              placeholder={t('onboarding.brandName.placeholder')}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              {t('onboarding.email')} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('onboarding.email.placeholder')}
              required
              disabled={loading}
            />
            <small className="field-hint">✅ {t('onboarding.email.hint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              {t('onboarding.password')} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('onboarding.password.placeholder')}
              required
              minLength="6"
              disabled={loading}
            />
            <small className="field-hint">{t('onboarding.password.hint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="telegramId">
              {t('onboarding.telegramId')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="telegramId"
              name="telegramId"
              value={formData.telegramId}
              onChange={handleInputChange}
              placeholder={t('onboarding.telegramId.placeholder')}
              required
              disabled={loading}
            />
            <small className="field-hint">
              {t('onboarding.telegramId.hint').replace('@get_myidrobot', '')}
              <a href="https://t.me/get_myidrobot" target="_blank" rel="noopener noreferrer" className="telegram-link">@get_myidrobot</a>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="facebookUrl">
              {t('onboarding.facebook')}
            </label>
            <input
              type="url"
              id="facebookUrl"
              name="facebookUrl"
              value={formData.facebookUrl}
              onChange={handleInputChange}
              placeholder={t('onboarding.facebook.placeholder')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="instagramUrl">
              {t('onboarding.instagram')}
            </label>
            <input
              type="url"
              id="instagramUrl"
              name="instagramUrl"
              value={formData.instagramUrl}
              onChange={handleInputChange}
              placeholder={t('onboarding.instagram.placeholder')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>{t('onboarding.messageLanguage')} <span className="required">*</span></label>
            <small className="field-hint" style={{ display: 'block', marginBottom: '10px' }}>
              {t('onboarding.messageLanguage.hint')}
            </small>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: formData.messageLanguage === 'ar' ? '2px solid #6272a4' : '2px solid #44475a',
                  background: formData.messageLanguage === 'ar' ? 'rgba(98,114,164,0.2)' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#f8f8f2',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="radio"
                  name="messageLanguage"
                  value="ar"
                  checked={formData.messageLanguage === 'ar'}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                {t('onboarding.messageLanguage.ar')}
              </label>
              <label
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: formData.messageLanguage === 'en' ? '2px solid #6272a4' : '2px solid #44475a',
                  background: formData.messageLanguage === 'en' ? 'rgba(98,114,164,0.2)' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#f8f8f2',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="radio"
                  name="messageLanguage"
                  value="en"
                  checked={formData.messageLanguage === 'en'}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                {t('onboarding.messageLanguage.en')}
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? `⏳ ${t('onboarding.submitting')}` : `✅ ${t('onboarding.submit')}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShopifyOnboardingPage;
